import { useEffect, useState } from 'react';

// Evento no estándar de Chromium para instalar PWAs.
interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

function getStandalone(): boolean {
  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    // iOS Safari
    (window.navigator as any).standalone === true
  );
}

function getIsIOS(): boolean {
  const ua = window.navigator.userAgent;
  const isIOSDevice = /iphone|ipad|ipod/i.test(ua);
  // iPad con iPadOS 13+ se reporta como Mac con pantalla táctil
  const isIPadOS = /macintosh/i.test(ua) && 'ontouchend' in document;
  return isIOSDevice || isIPadOS;
}

export interface PWAInstall {
  /** Hay un prompt nativo disponible (Chrome/Edge/Android). */
  canInstall: boolean;
  /** La app ya está instalada / corriendo como app. */
  isInstalled: boolean;
  /** Dispositivo iOS: no hay prompt nativo, requiere instrucciones manuales. */
  isIOS: boolean;
  /** Lanza el prompt nativo. Devuelve true si el usuario aceptó. */
  promptInstall: () => Promise<boolean>;
}

export function usePWAInstall(): PWAInstall {
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [isInstalled, setIsInstalled] = useState<boolean>(() => getStandalone());
  const isIOS = getIsIOS();

  useEffect(() => {
    const onBeforeInstall = (e: Event) => {
      e.preventDefault();
      setDeferredPrompt(e as BeforeInstallPromptEvent);
    };
    const onInstalled = () => {
      setIsInstalled(true);
      setDeferredPrompt(null);
    };
    const mq = window.matchMedia('(display-mode: standalone)');
    const onDisplayChange = () => setIsInstalled(getStandalone());

    window.addEventListener('beforeinstallprompt', onBeforeInstall);
    window.addEventListener('appinstalled', onInstalled);
    mq.addEventListener?.('change', onDisplayChange);

    return () => {
      window.removeEventListener('beforeinstallprompt', onBeforeInstall);
      window.removeEventListener('appinstalled', onInstalled);
      mq.removeEventListener?.('change', onDisplayChange);
    };
  }, []);

  const promptInstall = async (): Promise<boolean> => {
    if (!deferredPrompt) return false;
    await deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    setDeferredPrompt(null);
    return outcome === 'accepted';
  };

  return {
    canInstall: !!deferredPrompt && !isInstalled,
    isInstalled,
    isIOS,
    promptInstall,
  };
}
